import {
  filter,
  find,
  forEach,
  head,
  sumBy,
  without,
  isEmpty
} from "lodash";
import getSymbolFromCurrency from 'currency-symbol-map';
import Big from "big.js";
import { CURRENCY_SYMBOL_BLACK_LIST } from '../constants/SymbolBlackList';
import { pluck } from '../util/utils';

export const buildDataForPDF = (order, retailerInfo) => {
  const orderData = {
    delivery_method: order.delivery_method,
    order_id: order.id,
    sequential_number: order.sequential_number,
    web_id: order.web_id,
    created_at: order.created_at,
    retailer_tax: retailerInfo.tax_amount,
    subtotal: Big(Number(order.subtotal)).plus(Number(order.total_discount)).round(2).toNumber(),
    shipping_value: order.shipping_value,
    tax: order.tax_amount,
    total: order.total,
    notes: order.notes,
    terms_and_conditions: order.terms_and_conditions,
    terms_and_condition: order.terms_and_condition,
    currency: order.currency,
    custom_answers: order.custom_answers,
    products: [],
    address: order.address,
    city: order.city,
    state: order.state,
    country_id: order.country_id,
    zip_code: order.zip_code,
    discount_type: order.discount_type,
    discount_value: order.discount_value,
    discount_reason: order.discount_reason,
    total_discount: order.total_discount,
    subtotal_with_discount: order.subtotal,
    customer: order.customer
  };

  forEach(order.order_items, (product) => {
    const {
      id, title, quantity, unit_price, custom_tax, total, variant_name, sku, discount_type,
      discount_value, discount_reason, total_discount
    } = product;
    orderData.products.push({
      id,
      title,
      variant_name,
      quantity,
      unit_price,
      custom_tax,
      total,
      sku,
      discount_type,
      discount_value,
      discount_reason,
      total_discount,
      variants: product.order_item_variants || []
    });
  });

  return orderData;
};

export const orderCurrencySymbol = (order) => {
  if (!order) return '';

  const currency = getCurrency(order);

  return getSymbolFromCurrency(currency);
};

export const currencySymbolForPDF = (order) => {
  const currency = getCurrency(order);

  if (CURRENCY_SYMBOL_BLACK_LIST.includes(currency)) return '';

  return getSymbolFromCurrency(currency);
};

export const formatDataToSave = (data = {}) => {
  let formData = {
    terms_and_condition_attributes: {},
    custom_answers_attributes: [],
    order_items_attributes: [],
    payment_transactions_attributes: []
  };

  formData = addOrderInfo(formData, data);
  formData = addCustomerInfo(formData, data);
  formData = addCustomAnswersAttributes(formData, data);
  formData = addOrderItemsAttributes(formData, data);
  formData = addTermsAndConditionAttributes(formData, data);
  formData = addPaymentTransactionsAttributes(formData, data);

  return formData;
};

const addOrderInfo = (formData, data) => {
  const orderInfo = {
    ...formData,
    archived: data.archived,
    status: data.status,
    sales_channel_id: data.sales_channel_id,
    notes: data.notes,
    agent_name: data.agent_name,
    agent_id: data.agent_id,
    agent_email: data.agent_email,
    shipping_value: data.shipping_value === '' ? 0 : data.shipping_value,
    shipping_cost_id: data.shipping_cost_id === 'custom' ? null : data.shipping_cost_id,
    delivery_method: data.delivery_method,
    address: data.address,
    description: data.description,
    city: data.city,
    state: data.state,
    zip_code: data.zip_code,
    country_id: data.country_id,
    currency: data.currency,
    is_new_address: data.is_new_address || false,
    discount_type: data.discount_type,
    discount_value: data.discount_value,
    discount_reason: data.discount_reason,
    delivery_status: data.delivery_status || 'delivery_pending',
    delivery_attachment: data.delivery_attachment_file || undefined,
    remove_delivery_attachment: data.remove_delivery_attachment
  };

  return orderInfo;
};

const isPhoneMasked = (phone) => typeof phone === 'string' && phone.includes('*');

const hasValidPhone = (phone) => phone != null && String(phone).trim() !== '' && !isPhoneMasked(phone);

const addCustomerInfo = (formData, data) => {
  const { id_in_mercately, web_id_in_mercately, id_in_shops } = data.customer;
  const phone = hasValidPhone(data.customer.phone) ? data.customer.phone : undefined;

  const customerAttrs = {
    first_name: data.customer.first_name,
    last_name: data.customer.last_name,
    username: data.customer.username,
    email: data.customer.email,
    country_id: data.customer.country_id,
    id_type: data.customer.id_type,
    id_number: data.customer.id_number,
    address: data.customer.address,
    city: data.customer.city,
    state: data.customer.state,
    zip_code: data.customer.zip_code,
    username: data.customer.username
  };

  if (phone !== undefined) customerAttrs.phone = phone;
  if (id_in_shops) customerAttrs.id = id_in_shops;
  if (id_in_mercately) customerAttrs.id_in_mercately = id_in_mercately;
  if (web_id_in_mercately) customerAttrs.web_id_in_mercately = web_id_in_mercately;

  return {
    ...formData,
    customer_attributes: customerAttrs
  };
};

const addCustomAnswersAttributes = (formData, data) => {
  forEach(data.custom_answers, (ca) => {
    formData.custom_answers_attributes.push({
      id: ca.id,
      answer: typeof ca?.answer === 'string' ? (ca.answer?.trim() || '') : ca?.answer,
      custom_field_id: ca.custom_field_id,
      _destroy: false
    });
  });

  return formData;
};

const addOrderItemsAttributes = (formData, data) => {
  const newOrderItems = [];

  forEach(data.order_items, (item) => {
    const newOrderItem = {
      id: item.id || null,
      product_id: item.product_id,
      product_variant_combination_id: item.product_variant_combination_id,
      title: item.title,
      variant_name: item.variant_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      custom_tax: item.custom_tax,
      sold_wholesale: item.sold_wholesale,
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      discount_reason: item.discount_reason,
      _destroy: !!item._destroy,
      order_item_variants_attributes: []
    };

    if (!newOrderItem._destroy) {
      forEach(item.selectedVariants, (variant) => {
        newOrderItem.order_item_variants_attributes.push({
          id: variant.order_item_variant_id || null,
          product_variant_option_id: variant.variant_option_id,
          _destroy: !!variant._destroy
        });
      });
    }

    newOrderItems.push(newOrderItem);
  });

  forEach(data.removedOrderItems, (item) => {
    newOrderItems.push({
      id: item.id,
      _destroy: true
    });
  });

  // const { unifyOrderItems } = unifySimilarProducts(newOrderItems);

  formData.order_items_attributes = newOrderItems;

  return formData;
};

const addPaymentTransactionsAttributes = (formData, data) => {
  if (!isEmpty(data.paymentTransaction)) {
    const {
      id, status, payment_method, custom_payment_method, note, selectedFile, remove_attachment, service
    } = data.paymentTransaction;

    if (!id && status === 'pending') return formData;

    const newTransaction = {
      status, payment_method, custom_payment_method, note, service
    };

    if (selectedFile) newTransaction.attachment = selectedFile;
    if (remove_attachment) newTransaction.remove_attachment = true;
    if (id) newTransaction.id = id;
    if (data.orderSummary) newTransaction.amount = data.orderSummary.total;

    formData.payment_transactions_attributes = [newTransaction];
  }

  return formData;
};

const unifySimilarProducts = (orderItems) => {
  const unifyOrderItems = [];

  let newOrderItems = [...orderItems];
  let filteredItems = [];

  while (newOrderItems.length > 0) {
    const orderItem = newOrderItems[0];

    if (orderItem._destroy) {
      unifyOrderItems.push(orderItem);
      filteredItems = [orderItem];
    } else {
      filteredItems = filterItems(orderItems, orderItem);

      if (shouldUnifySavedItems(filteredItems)) {
        unifyItems(filteredItems, unifyOrderItems, orderItem);
        addItemsToRemoveFromBack(filteredItems, unifyOrderItems);
      } else {
        const firstItem = head(filteredItems);
        unifyOrderItems.push({ ...firstItem, quantity: sumQuantities(filteredItems) });
      }
    }

    newOrderItems = removeAddedProduct(newOrderItems, filteredItems);
  }

  return { unifyOrderItems };
};

const filterItems = (orderItems, orderItem) => filter(orderItems, (item) => (
  !item._destroy
  && item.product_id === orderItem.product_id
  && item.title === orderItem.title
  && Number(item.unit_price) === Number(orderItem.unit_price)
  && Number(item.custom_tax) === Number(orderItem.custom_tax)
  && hasSameVariants(orderItem.order_item_variants_attributes, item.order_item_variants_attributes)
));

const hasSameVariants = (array1, array2) => {
  const filteredArray1 = filterItemDestroyed(array1, true);
  const filteredArray2 = filterItemDestroyed(array2, true);

  if (filteredArray1.length !== filteredArray2.length) return false;

  const ids1 = pluck(filteredArray1, 'product_variant_option_id');
  const ids2 = pluck(filteredArray2, 'product_variant_option_id');

  return ids1.sort().every((element, index) => element === ids2.sort()[index]);
};

const shouldUnifySavedItems = (filteredItems) => (
  filteredItems.length > 1
  && find(filteredItems, (o) => o.id != null || o.id !== undefined)
);

const unifyItems = (filteredItems, unifyOrderItems, orderItem) => {
  const firstItem = head(filteredItems);

  const newOrderItemVariants = filterItemDestroyed(orderItem.order_item_variants_attributes, true)
    .map((item) => ({ product_variant_option_id: item.product_variant_option_id }));

  const newItem = {
    ...firstItem,
    id: null,
    quantity: sumQuantities(filteredItems),
    order_item_variants_attributes: newOrderItemVariants,
    _destroy: false
  };

  unifyOrderItems.push(newItem);
};

const sumQuantities = (filteredItems) => sumBy(filteredItems, (item) => Number(item.quantity));

const addItemsToRemoveFromBack = (filteredItems, unifyOrderItems) => {
  forEach(filteredItems, (item) => {
    if (item.id) {
      unifyOrderItems.push({
        ...item,
        _destroy: true
      });
    }
  });
};

const removeAddedProduct = (newOrderItems, filteredItems = []) => {
  forEach(filteredItems, (item) => {
    newOrderItems = without(newOrderItems, item);
  });

  return newOrderItems;
};

const filterItemDestroyed = (array, filter) => array.filter((el) => el._destroy !== filter);

function getCurrency(order) {
  const currency = order?.payment_transaction?.currency || order.currency || ENV.CURRENCY || 'USD';
  return currency;
}

const addTermsAndConditionAttributes = (formData, data) => ({
  ...formData,
  terms_and_condition_attributes: {
    ...data.terms_and_condition
  }
});
